import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {User, Heart, MessageSquare, LogOut, Edit} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'

interface UserProfile {
  _id: string
  name?: string
  email?: string
  phone?: string
  role?: string
}

interface Prayer {
  _id: string
  message: string
  type?: string
  createdAt: string
  status: string
}

interface Testimonial {
  _id: string
  message: string
  status: string
  createdAt: string
}

const MyAccount: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [prayers, setPrayers] = useState<Prayer[]>([])
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [formData, setFormData] = useState({
    nome: '',
    telefone: ''
  })

  useEffect(() => {
    loadUserData()
  }, [])

  const loadUserData = async () => {
    try {
      setLoading(true)
      const currentUser = lumi.auth.user
      
      if (!currentUser?.userId) {
        console.log('Usuário não autenticado, redirecionando...')
        window.location.href = '/login'
        return
      }

      console.log('Carregando dados para:', currentUser.email)

      // Buscar dados do usuário pelo email (campo único)
      const { list: userList } = await lumi.entities.users.list({
        filter: { email: currentUser.email }
      })
      
      console.log('Usuários encontrados:', userList)
      
      if (userList && userList.length > 0) {
        const userData = userList[0]
        setUser(userData)
        setFormData({
          nome: userData.name || '',
          telefone: userData.phone || ''
        })
      } else {
        console.log('Nenhum usuário encontrado no banco')
        toast.error('Perfil não encontrado no sistema')
      }

      // Buscar pedidos de oração do usuário pelo email
      try {
        const { list: prayerList } = await lumi.entities.prayers.list({
          filter: { email: currentUser.email },
          sort: { createdAt: -1 }
        })
        console.log('Pedidos de oração encontrados:', prayerList)
        setPrayers(prayerList || [])
      } catch (prayerError) {
        console.error('Erro ao buscar orações:', prayerError)
        setPrayers([])
      }

      // Buscar testemunhos do usuário
      try {
        const { list: testimonialList } = await lumi.entities.testimonials.list({
          filter: { 
            $or: [
              { creator: currentUser.userId },
              { email: currentUser.email }
            ]
          },
          sort: { createdAt: -1 }
        })
        console.log('Testemunhos encontrados:', testimonialList)
        setTestimonials(testimonialList || [])
      } catch (testimonialError) {
        console.error('Erro ao buscar testemunhos:', testimonialError)
        setTestimonials([])
      }

    } catch (error) {
      console.error('Erro detalhado ao carregar dados:', error)
      toast.error('Erro ao carregar seus dados')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (!user?._id) {
        toast.error('Usuário não encontrado')
        return
      }

      console.log('Atualizando usuário:', user._id)
      console.log('Dados para atualizar:', { name: formData.nome, phone: formData.telefone })

      const updateData = {
        name: formData.nome.trim() || user.name,
        phone: formData.telefone.trim() || user.phone || '',
        email: user.email,
        role: user.role || 'USER',
        updatedAt: new Date().toISOString()
      }

      const result = await lumi.entities.users.update(user._id, updateData)
      console.log('Resultado da atualização:', result)

      toast.success('Perfil atualizado com sucesso!')
      setEditMode(false)
      await loadUserData()
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error)
      toast.error(`Erro ao atualizar perfil: ${error.message || 'Tente novamente'}`)
    }
  }

  const handleLogout = async () => {
    try {
      await lumi.auth.signOut()
      window.location.href = '/'
    } catch (error) {
      console.error('Erro ao sair:', error)
      toast.error('Erro ao fazer logout')
    }
  }

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase()
    switch (statusLower) {
      case 'aprovado':
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'pendente':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'rejeitado':
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'oculto':
      case 'hidden':
        return 'bg-gray-100 text-gray-800'
      case 'deleted':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatStatus = (status: string) => {
    const statusLower = status?.toLowerCase()
    const statusMap: Record<string, string> = {
      'approved': 'Aprovado',
      'pending': 'Pendente',
      'rejected': 'Rejeitado',
      'hidden': 'Oculto',
      'deleted': 'Excluído'
    }
    return statusMap[statusLower] || status
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="bg-white w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <User size={48} className="text-[#1e3a8a]" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {user?.name || 'Minha Conta'}
            </h1>
            <p className="text-white/90 text-lg">{user?.email}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - Perfil */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#1e3a8a]">Meu Perfil</h2>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className="text-[#6b8f71] hover:text-[#5a7a61]"
                >
                  <Edit size={20} />
                </button>
              </div>

              {editMode ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-[#1e3a8a] text-white py-2 rounded-lg hover:bg-[#2d4a9a] transition"
                    >
                      Salvar
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Nome</p>
                    <p className="font-semibold text-gray-900">{user?.name || 'Não informado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="font-semibold text-gray-900">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Telefone</p>
                    <p className="font-semibold text-gray-900">{user?.phone || 'Não informado'}</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="w-full mt-6 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
              >
                <LogOut size={18} />
                Sair
              </button>
            </div>
          </motion.div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Meus Pedidos de Oração */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Heart className="text-[#6b8f71]" size={24} />
                  <h2 className="text-2xl font-bold text-[#1e3a8a]">Meus Pedidos de Oração</h2>
                </div>

                {prayers.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">
                    Você ainda não enviou nenhum pedido de oração
                  </p>
                ) : (
                  <div className="space-y-4">
                    {prayers.map((prayer) => (
                      <div key={prayer._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${getStatusColor(prayer.status)}`}>
                            {formatStatus(prayer.status)}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(prayer.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        {prayer.type && (
                          <p className="text-sm text-[#6b8f71] font-semibold mb-2">
                            Tipo: {prayer.type}
                          </p>
                        )}
                        <p className="text-gray-700">{prayer.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Meus Testemunhos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="text-[#6b8f71]" size={24} />
                  <h2 className="text-2xl font-bold text-[#1e3a8a]">Meus Testemunhos</h2>
                </div>

                {testimonials.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">
                    Você ainda não enviou nenhum testemunho
                  </p>
                ) : (
                  <div className="space-y-4">
                    {testimonials.map((testimonial) => (
                      <div key={testimonial._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${getStatusColor(testimonial.status)}`}>
                            {formatStatus(testimonial.status)}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(testimonial.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                        <p className="text-gray-700">{testimonial.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyAccount
